import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { Analytics } from '../utils/analytics';

interface RegisterProps {
  onRegister: () => void;
}

const Register: React.FC<RegisterProps> = ({ onRegister }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [userId, setUserId] = useState('');
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/register', {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName
      });

      if (response.data.success) {
        if (response.data.requiresVerification) {
          // Email-Verifizierung erforderlich
          setUserId(response.data.data.userId);
          setRequiresVerification(true);
        } else {
          // Direkt einloggen (Fallback falls 2FA deaktiviert)
          localStorage.setItem('accessToken', response.data.data.accessToken);
          localStorage.setItem('refreshToken', response.data.data.refreshToken);
          localStorage.setItem('user', JSON.stringify(response.data.data.user));
          onRegister();
          navigate('/dashboard');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/verify-code', { 
        userId, 
        code: verificationCode 
      });
      
      if (response.data.success) {
        localStorage.setItem('accessToken', response.data.data.accessToken);
        localStorage.setItem('refreshToken', response.data.data.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
        Analytics.userRegistered();
        onRegister();
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Code-Verifizierung fehlgeschlagen. Bitte versuche es erneut.');
      setVerificationCode(''); // Code zurücksetzen für neuen Versuch
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '450px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '30px'
        }}>
          <h1 style={{ 
            margin: 0,
            background: 'linear-gradient(135deg, #0f2027, #2c5364)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: '32px',
            fontWeight: 'bold'
          }}>
            {requiresVerification ? '📧 Email bestätigen' : 'Registrierung'}
          </h1>
          {!requiresVerification && (
            <p style={{ color: '#6c757d', marginTop: '10px', fontSize: '14px' }}>
              Erstelle dein kostenloses Konto
            </p>
          )}
        </div>

        {!requiresVerification ? (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#2c5364', fontWeight: '600', fontSize: '14px' }}>Vorname</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e9ecef',
                  borderRadius: '12px',
                  fontSize: '15px',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#2c5364'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#2c5364', fontWeight: '600', fontSize: '14px' }}>Nachname</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e9ecef',
                  borderRadius: '12px',
                  fontSize: '15px',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#2c5364'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#2c5364', fontWeight: '600', fontSize: '14px' }}>E-Mail</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e9ecef',
                  borderRadius: '12px',
                  fontSize: '15px',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#2c5364'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#2c5364', fontWeight: '600', fontSize: '14px' }}>Passwort</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e9ecef',
                  borderRadius: '12px',
                  fontSize: '15px',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#2c5364'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
              />
              <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>Mindestens 6 Zeichen</small>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#2c5364', fontWeight: '600', fontSize: '14px' }}>Passwort bestätigen</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e9ecef',
                  borderRadius: '12px',
                  fontSize: '15px',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#2c5364'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
              />
            </div>

            {error && (
              <div style={{
                background: '#fee',
                color: '#c33',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '15px',
                fontSize: '14px',
                border: '1px solid #fcc'
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #0f2027, #2c5364)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: loading ? 0.7 : 1
              }}
              onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {loading ? 'Lädt...' : 'Kostenlos registrieren'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode}>
            <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}>
              Wir haben einen 6-stelligen Code an <strong>{formData.email}</strong> gesendet.
              Bitte gib den Code ein, um deine E-Mail-Adresse zu bestätigen.
            </p>

            <div className="form-group">
              <label>Bestätigungscode</label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                maxLength={6}
                required
                disabled={loading}
                autoFocus
                style={{ 
                  fontSize: '24px', 
                  letterSpacing: '8px', 
                  textAlign: 'center',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            {error && <div className="error">{error}</div>}

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', marginTop: '10px' }}
              disabled={loading || verificationCode.length !== 6}
            >
              {loading ? 'Prüfe Code...' : 'Bestätigen'}
            </button>

            <button
              type="button"
              onClick={() => {
                setRequiresVerification(false);
                setVerificationCode('');
                setError('');
              }}
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                color: '#2c5364',
                border: '2px solid #e9ecef',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginTop: '10px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#2c5364';
                e.currentTarget.style.background = 'rgba(44, 83, 100, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e9ecef';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              ← Zurück zur Registrierung
            </button>
          </form>
        )}

        {!requiresVerification && (
          <div style={{
            textAlign: 'center',
            marginTop: '25px',
            paddingTop: '20px',
            borderTop: '1px solid #e9ecef'
          }}>
            <p style={{ color: '#6c757d', fontSize: '14px', margin: 0 }}>
              Bereits registriert?{' '}
              <Link 
                to="/login" 
                style={{ 
                  color: '#2c5364',
                  fontWeight: 'bold',
                  textDecoration: 'none'
                }}
              >
                Jetzt anmelden
              </Link>
            </p>
          </div>
        )}

        <div style={{
          textAlign: 'center',
          marginTop: '20px',
          fontSize: '12px'
        }}>
          <Link to="/imprint" style={{ color: '#999', textDecoration: 'none', marginRight: '15px' }}>Impressum</Link>
          <Link to="/privacy" style={{ color: '#999', textDecoration: 'none' }}>Datenschutz</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
